import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { useSelector } from "react-redux";
import { Navigate, Link, useParams } from "react-router-dom";
import { Button, Input, Modal, Tag, message } from "antd";
import NewMiniDrawer from "../../../components/navMenu/menu-nav";
import MapaRota from "../../../components/MapaRota";
import LinhaDoTempoFrete from "../../../components/LinhaDoTempoFrete";
import LinkPublicoDoFrete from "../../../components/LinkPublicoDoFrete";
import freightService from "../../../service/freight.service";
import acompanhamentoApi from "../../../service/modules/acompanhamento.api";
import canhotoApi from "../../../service/modules/canhoto.api";
import {
  marcacaoDoFrete,
  CORES_DA_MARCACAO,
  ROTULOS_DA_MARCACAO,
} from "../../../util/freight/acompanhamento-marcacoes";
import {
  CORES_DO_ALERTA,
  detalheDoAlerta,
  ordenarAlertas,
  rotuloDoAlerta,
} from "../../../util/freight/alertas-do-frete";
import {
  localDoCanhoto,
  referenciaDoCanhoto,
  textoDaDistancia,
  textoDaPrecisao,
  textoDoLocal,
} from "../../../util/freight/localizacao-do-canhoto";
import useRecargaPeriodica from "../../../util/freight/useRecargaPeriodica";
import { translatorForTheStatusOfStoppingPoints } from "../../frete-copy/action/action";

/**
 * Cabeçalho, status e ocorrências se renovam sozinhos. Pontos de parada e linha do tempo já chegam
 * por escuta; as ocorrências vêm do backend, calculadas com a hora do servidor, e precisam ser
 * pedidas de novo.
 */
const RECARGA_DO_DETALHE_MS = 60 * 1000;

/**
 * Classificações que a conferência automática grava. Só sobre uma delas faz sentido aprovar exceção:
 * `CONFERIDO` bateu dentro do raio e `EXCECAO_APROVADA` já foi aprovada.
 */
const PRECISA_DE_EXCECAO = [
  "FORA_DO_RAIO",
  "SEM_REFERENCIA",
  "PRECISAO_INSUFICIENTE",
  "SEM_LOCALIZACAO",
];

const ROTULO_DO_CANHOTO = {
  CONFERIDO: "Canhoto conferido",
  FORA_DO_RAIO: "Canhoto fora do raio da entrega",
  SEM_REFERENCIA: "Canhoto sem endereço de referência",
  PRECISAO_INSUFICIENTE: "Canhoto com localização imprecisa",
  SEM_LOCALIZACAO: "Canhoto sem localização",
  EXCECAO_APROVADA: "Exceção aprovada",
};

const COR_DO_CANHOTO = {
  CONFERIDO: "green",
  EXCECAO_APROVADA: "blue",
};

const MINIMO_DA_JUSTIFICATIVA = 10;

function paraMillis(valor) {
  if (valor == null) return null;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor.toMillis === "function") return valor.toMillis();
  if (typeof valor.seconds === "number") {
    return valor.seconds * 1000 + Math.floor((valor.nanoseconds || 0) / 1e6);
  }
  const d = new Date(valor).getTime();
  return Number.isNaN(d) ? null : d;
}

function dataHora(valor) {
  const ms = paraMillis(valor);
  return ms ? new Date(ms).toLocaleString("pt-BR") : "—";
}

function statusDoPonto(ponto) {
  const bruto =
    ponto?.typeOperation === "UNLOADING"
      ? ponto?.unloadingStatus
      : ponto?.loadingStatus;
  const traduzido = translatorForTheStatusOfStoppingPoints(
    String(bruto || "").toUpperCase(),
  );
  return Array.isArray(traduzido) ? "—" : traduzido;
}

function endereco(ponto) {
  const partes = [
    [ponto?.logradouro, ponto?.number].filter(Boolean).join(", "),
    [ponto?.city, ponto?.uf].filter(Boolean).join(" - "),
  ].filter(Boolean);
  return partes.join(" · ") || "Endereço não informado";
}

/** O canhoto como o mapa desenha. Mesmo critério do bloco na lista: sem classificação, não aparece. */
function canhotoNoMapa(ponto) {
  const status = String(ponto?.receiptStatus || "");
  const local = localDoCanhoto(ponto);
  if (!status || !local) return null;

  const referencia = referenciaDoCanhoto(ponto);
  return {
    id: ponto.id,
    latitude: local.latitude,
    longitude: local.longitude,
    precisao: local.precisao,
    referencia: referencia ? [referencia.latitude, referencia.longitude] : null,
    titulo: `${ponto.stop_order}. ${ponto.name || "Ponto de parada"}`,
    linhas: [
      ROTULO_DO_CANHOTO[status] || status,
      [
        textoDaDistancia(ponto.receiptDistanceMeters),
        textoDaPrecisao(local.precisao),
      ]
        .filter(Boolean)
        .join(" · "),
      ponto.receiptCheckedAt
        ? `Conferido em ${dataHora(ponto.receiptCheckedAt)}`
        : null,
    ].filter(Boolean),
  };
}

function DetalheDoFrete() {
  const { id } = useParams();
  const logado = useSelector((state) => state.usuarioLogado);

  const [frete, setFrete] = useState(null);
  const [pontos, setPontos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [pontoEmExcecao, setPontoEmExcecao] = useState(null);
  const [justificativa, setJustificativa] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [foco, setFoco] = useState(null);
  const secaoDoMapaRef = useRef(null);

  useEffect(() => {
    if (!id) return undefined;

    let cancelado = false;
    setCarregando(true);
    setErro(null);

    acompanhamentoApi
      .buscarFrete(id)
      .then((resposta) => {
        if (cancelado) return;
        setFrete(resposta?.data?.data || null);
      })
      .catch((e) => {
        if (cancelado) return;
        setErro(
          e?.response?.status === 404
            ? "Frete não encontrado."
            : "Não foi possível carregar este frete.",
        );
        console.error("Erro ao carregar o frete:", e);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;

    // Trocando de frete sem sair da tela, os canhotos do anterior não podem chegar ao mapa do novo.
    setPontos([]);
    setFoco(null);
    const cancelar = freightService.subscribeStoppingPointsByFreight(
      id,
      (snap) =>
        setPontos(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))),
      (e) => console.error("Erro ao carregar os pontos de parada:", e),
    );

    return () => cancelar && cancelar();
  }, [id]);

  // O id vale até a resposta voltar: sem isto, a recarga de um frete que o operador acabou de trocar
  // pintaria o anterior na tela.
  const idRef = useRef(id);
  useEffect(() => {
    idRef.current = id;
  }, [id]);

  // Só troca o frete no sucesso. O corpo desmonta o mapa enquanto `carregando` ou `erro` estão
  // ligados, então a recarga não toca nos dois — o mapa refaria a viagem inteira a cada minuto.
  const recarregarFrete = useCallback(async () => {
    if (!id || carregando || erro) return;
    try {
      const resposta = await acompanhamentoApi.buscarFrete(id);
      const atualizado = resposta?.data?.data;
      if (atualizado && idRef.current === id) setFrete(atualizado);
    } catch (e) {
      console.error("Erro ao recarregar o frete:", e);
    }
  }, [id, carregando, erro]);

  useRecargaPeriodica(recarregarFrete, RECARGA_DO_DETALHE_MS);

  const marcacao = useMemo(
    () => (frete ? marcacaoDoFrete(frete) : null),
    [frete],
  );

  const alertas = useMemo(() => ordenarAlertas(frete?.alertas), [frete]);

  // `null` até a escuta trazer os pontos: é nessa hora que o mapa abre a vista para os canhotos, e uma
  // vez só. Com a lista vazia ele gastaria essa vez antes de os pontos chegarem.
  const canhotos = useMemo(
    () => (pontos.length ? pontos.map(canhotoNoMapa).filter(Boolean) : null),
    [pontos],
  );

  const verNoMapa = (pontoId) => {
    secaoDoMapaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setFoco((anterior) => ({ id: pontoId, vez: (anterior?.vez || 0) + 1 }));
  };

  const canhotoDaExcecao = String(pontoEmExcecao?.receiptStatus || "");
  const localDaExcecao = localDoCanhoto(pontoEmExcecao);
  const distanciaDaExcecao = textoDaDistancia(
    pontoEmExcecao?.receiptDistanceMeters,
  );

  const aprovarExcecao = async () => {
    if (!pontoEmExcecao) return;

    setEnviando(true);
    try {
      await canhotoApi.aprovarExcecao({
        freightId: id,
        pointId: pontoEmExcecao.id,
        justification: justificativa.trim(),
      });
      message.success("Exceção aprovada.");
      setPontoEmExcecao(null);
      setJustificativa("");
    } catch (e) {
      const doBackend = e?.response?.data?.message;
      message.error(doBackend || "Não foi possível aprovar a exceção.");
      console.error("Erro ao aprovar exceção de canhoto:", e);
    } finally {
      setEnviando(false);
    }
  };

  const conteudo = (
    <div className="detalhe">
      <header className="detalhe__cabecalho">
        <div>
          <Link className="detalhe__voltar" to="/freightsDetail">
            ‹ Acompanhamento de entregas
          </Link>
          <h1>
            Frete {frete?.numberSerial ?? id}
            {marcacao && (
              <Tag color={CORES_DA_MARCACAO[marcacao]}>
                {ROTULOS_DA_MARCACAO[marcacao]}
              </Tag>
            )}
          </h1>
          <p className="detalhe__resumo">
            {frete?.shipper?.name || "Embarcador não informado"} ·{" "}
            {frete?.driver?.name ||
              frete?.driver?.driverName ||
              "Motorista não informado"}
            {frete?.plate ? ` · ${frete.plate}` : ""} ·{" "}
            {frete?.firstDelivery?.city || "—"} -{" "}
            {frete?.firstDelivery?.uf || "—"} →{" "}
            {frete?.lastDelivery?.city || "—"} -{" "}
            {frete?.lastDelivery?.uf || "—"}
          </p>
        </div>
      </header>

      {erro ? (
        <p className="detalhe__erro">{erro}</p>
      ) : carregando ? (
        <p className="detalhe__vazio">Carregando o frete...</p>
      ) : (
        <>
          <section className="detalhe__mapa" ref={secaoDoMapaRef}>
            <h2>Trajeto percorrido</h2>
            <MapaRota frete={frete} canhotos={canhotos} foco={foco} />
          </section>

          <LinkPublicoDoFrete freteId={id} numeroDoFrete={frete?.numberSerial} />

          <section className="detalhe__bloco detalhe__alertas">
            <h2>Ocorrências</h2>
            {!alertas.length ? (
              <p className="detalhe__vazio">
                Nenhuma ocorrência ativa neste frete.
              </p>
            ) : (
              <ul className="detalhe__lista-de-alertas">
                {alertas.map((alerta) => (
                  <li key={alerta.chave}>
                    <Tag color={CORES_DO_ALERTA[alerta.chave]}>
                      {rotuloDoAlerta(alerta)}
                    </Tag>
                    <span>{detalheDoAlerta(alerta)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <div className="detalhe__colunas">
            <section className="detalhe__bloco">
              <h2>Linha do tempo</h2>
              <LinhaDoTempoFrete frete={frete} />
            </section>

            <section className="detalhe__bloco">
              <h2>Pontos de parada</h2>
              {!pontos.length ? (
                <p className="detalhe__vazio">
                  Este frete não tem pontos de parada.
                </p>
              ) : (
                <ol className="detalhe__pontos">
                  {pontos.map((ponto) => {
                    const canhoto = String(ponto.receiptStatus || "");
                    const podeAprovar = PRECISA_DE_EXCECAO.includes(canhoto);
                    const distancia = textoDaDistancia(
                      ponto.receiptDistanceMeters,
                    );
                    const local = localDoCanhoto(ponto);

                    return (
                      <li key={ponto.id} className="detalhe__ponto">
                        <div className="detalhe__ponto-topo">
                          <strong>
                            {ponto.stop_order}.{" "}
                            {ponto.name || "Ponto de parada"}
                          </strong>
                          <span className="detalhe__ponto-tipo">
                            {ponto.typeOperation === "UNLOADING"
                              ? "Entrega"
                              : "Coleta"}
                          </span>
                        </div>
                        <span className="detalhe__ponto-endereco">
                          {endereco(ponto)}
                        </span>
                        <span className="detalhe__ponto-status">
                          {statusDoPonto(ponto)}
                          {ponto.date_operation
                            ? ` · ${ponto.date_operation}`
                            : ""}
                        </span>

                        {canhoto && (
                          <div className="detalhe__canhoto">
                            <Tag color={COR_DO_CANHOTO[canhoto] || "orange"}>
                              {ROTULO_DO_CANHOTO[canhoto] || canhoto}
                            </Tag>
                            {distancia && <span>{distancia}</span>}
                            {ponto.receiptCheckedAt && (
                              <span>
                                Conferido em {dataHora(ponto.receiptCheckedAt)}
                              </span>
                            )}
                            {ponto.receiptException && (
                              <span className="detalhe__canhoto-excecao">
                                “{ponto.receiptException.justification}” —{" "}
                                {dataHora(ponto.receiptException.approvedAt)}
                              </span>
                            )}
                            {podeAprovar && (
                              <Button
                                size="small"
                                onClick={() => {
                                  setPontoEmExcecao(ponto);
                                  setJustificativa("");
                                }}
                              >
                                Aprovar exceção
                              </Button>
                            )}
                            {local && (
                              <span className="detalhe__canhoto-local">
                                {textoDoLocal(local)}
                                <Button
                                  size="small"
                                  type="link"
                                  onClick={() => verNoMapa(ponto.id)}
                                >
                                  Ver no mapa
                                </Button>
                              </span>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>
        </>
      )}

      <Modal
        title="Aprovar exceção do canhoto"
        open={Boolean(pontoEmExcecao)}
        onCancel={() => setPontoEmExcecao(null)}
        onOk={aprovarExcecao}
        okText="Aprovar"
        cancelText="Cancelar"
        confirmLoading={enviando}
        okButtonProps={{
          disabled: justificativa.trim().length < MINIMO_DA_JUSTIFICATIVA,
        }}
      >
        {pontoEmExcecao && (
          <div className="detalhe__modal-ponto">
            <strong>
              {pontoEmExcecao.stop_order}.{" "}
              {pontoEmExcecao.name || "Ponto de parada"}
            </strong>
            <div className="detalhe__canhoto">
              <Tag color={COR_DO_CANHOTO[canhotoDaExcecao] || "orange"}>
                {ROTULO_DO_CANHOTO[canhotoDaExcecao] || canhotoDaExcecao}
              </Tag>
              {distanciaDaExcecao && <span>{distanciaDaExcecao}</span>}
            </div>
            {localDaExcecao && <span>{textoDoLocal(localDaExcecao)}</span>}
          </div>
        )}
        <p className="detalhe__modal-texto">
          A distância medida continua guardada junto da aprovação. Escreva por
          que a divergência é aceitável — é o que responde a pergunta meses
          depois, numa disputa de carga.
        </p>
        <Input.TextArea
          rows={4}
          maxLength={500}
          showCount
          placeholder="Ex.: recebedor orientou a descarregar no pátio anexo, a 1,8 km do endereço cadastrado."
          value={justificativa}
          onChange={(e) => setJustificativa(e.target.value)}
        />
      </Modal>
    </div>
  );

  return logado > 0 ? (
    <NewMiniDrawer divOpen={conteudo} />
  ) : (
    <Navigate to="/login" />
  );
}

export default DetalheDoFrete;
